import React, { useCallback, useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";

import {
  CONSTANT_LABELS,
  mapChannelToColor,
  METRICS_LABELS,
} from "@/constants/constants";
import { useChannelColorMap } from "@lib/hooks/use-channel-color-map";
import { useChannelNameMap } from "@lib/hooks/use-channel-name-map";
import { capitalizeFirstLetter } from "@lib/util-functions/string";
import type { KpisPerChannelResponse } from "@lib/api-hooks/useKpisPerChannel";
import type { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";
import useCurrency from "@lib/hooks/use-currency";
import useFormat from "@lib/hooks/use-format";
import {
  useDateRangeState,
  useNewVsReturningState,
} from "@lib/hooks/url-state";
import { formatGranularityTooltipLabel } from "@lib/util-functions";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ComparisonChartLegendContent,
  ComparisonChartTooltipContent,
} from "@tracify/ui/chart";
import { Skeleton } from "@tracify/ui/skeleton";

import {
  buildMarketingChannelOverviewChartData,
  getMarketingChannelOverviewYAxisWidth,
  type ChannelOverviewChartMetric,
  type ChannelOverviewSegment,
  getComparisonMetricKey,
  shouldUseIntegerYAxisTicks,
} from "./marketing-channel-overview-chart-utils";

type MarketingChannelOverviewChartProps = {
  data?: KpisPerChannelResponse;
  comparedData?: KpisPerChannelResponse;
  channels: string[];
  metric: ChannelOverviewChartMetric;
  granularity: Granularity;
  compared: boolean;
};

const CURRENCY_METRICS = new Set<ChannelOverviewChartMetric>([
  "customer_revenue",
  "total_spend",
  "average_order_value",
  "cost_per_order",
  "customer_acquisition_cost",
]);

const RATE_METRICS = new Set<ChannelOverviewChartMetric>([
  "conversion_rate",
  "new_customer_purchase_ratio",
  "new_visitor_pageview_ratio",
]);

const getGradientId = (channel: string) =>
  `channel-overview-gradient-${channel.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

const CHART_HEIGHT_CLASS = "h-[320px] lg:h-[360px]";

function ChartEmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={`${CHART_HEIGHT_CLASS} flex items-center justify-center text-sm text-muted-foreground text-center px-4`}
    >
      {children}
    </div>
  );
}

export function MarketingChannelOverviewChartSkeleton() {
  return (
    <Skeleton
      className={`${CHART_HEIGHT_CLASS} w-full rounded-lg bg-muted`}
      aria-label="Loading channel overview chart"
    />
  );
}

export function MarketingChannelOverviewChart({
  data,
  comparedData,
  channels,
  metric,
  granularity,
  compared,
}: MarketingChannelOverviewChartProps) {
  const { newVsReturning } = useNewVsReturningState();
  const { startTime, endTime } = useDateRangeState();
  const { calculateCurrencyConvertion } = useCurrency();
  const { formatNumber } = useFormat();
  const channelColorMap = useChannelColorMap();
  const channelNameMap = useChannelNameMap();
  const getChannelLabel = useCallback(
    (channel: string) =>
      channelNameMap[channel] ??
      CONSTANT_LABELS[channel] ??
      capitalizeFirstLetter(channel),
    [channelNameMap]
  );
  const segment = newVsReturning as ChannelOverviewSegment;

  const chartData = useMemo(
    () =>
      buildMarketingChannelOverviewChartData({
        data,
        comparedData: compared ? comparedData : undefined,
        channels,
        metric,
        segment,
        granularity,
      }),
    [channels, compared, comparedData, data, granularity, metric, segment]
  );

  const chartConfig = useMemo<ChartConfig>(() => {
    const config: ChartConfig = {};

    channels.forEach((channel) => {
      const label = getChannelLabel(channel);
      const color = mapChannelToColor(
        CONSTANT_LABELS[channel] ?? channel,
        channelColorMap
      );
      config[channel] = {
        label,
        color,
      };
      config[getComparisonMetricKey(channel)] = {
        label: `Previous ${label}`,
        color,
      };
    });

    return config;
  }, [channelColorMap, channels, getChannelLabel]);

  const formatValue = useCallback(
    (value: number) => {
      if (CURRENCY_METRICS.has(metric)) {
        return calculateCurrencyConvertion(value, 2, true);
      }

      if (RATE_METRICS.has(metric)) {
        return `${formatNumber(value * 100, 2)}%`;
      }

      if (metric === "return_on_ad_spend") {
        return formatNumber(value, 2);
      }

      return formatNumber(value, 0);
    },
    [calculateCurrencyConvertion, formatNumber, metric]
  );

  const formatAxisValue = useCallback(
    (value: number) => {
      if (CURRENCY_METRICS.has(metric)) {
        return calculateCurrencyConvertion(value, 0, true);
      }

      if (RATE_METRICS.has(metric)) {
        return `${formatNumber(value * 100, 0)}%`;
      }

      if (metric === "return_on_ad_spend") {
        return formatNumber(value, 1);
      }

      return formatNumber(value, 0);
    },
    [calculateCurrencyConvertion, formatNumber, metric]
  );

  const formatTooltipLabel = useCallback(
    (label: string | number | undefined) => {
      if (!label) return "";

      const labelValue = String(label);
      const firstDate = chartData[0]?.date;
      const lastDate = chartData[chartData.length - 1]?.date;

      return formatGranularityTooltipLabel({
        date: labelValue,
        granularity,
        isFirstTick: labelValue === firstDate,
        isLastTick: labelValue === lastDate,
        startDate: startTime,
        endDate: endTime,
      });
    },
    [chartData, endTime, granularity, startTime]
  );

  const yAxisWidth = useMemo(
    () =>
      getMarketingChannelOverviewYAxisWidth({
        chartData,
        formatAxisValue,
      }),
    [chartData, formatAxisValue]
  );
  const allowYAxisDecimals = !shouldUseIntegerYAxisTicks(metric);

  if (!data || Object.keys(data.channels).length === 0) {
    return (
      <ChartEmptyState>
        No channel data found for selected time-range.
      </ChartEmptyState>
    );
  }

  if (channels.length === 0) {
    return <ChartEmptyState>Select at least one channel.</ChartEmptyState>;
  }

  if (chartData.length === 0) {
    return (
      <ChartEmptyState>
        No {METRICS_LABELS[metric]} history found for selected time-range.
      </ChartEmptyState>
    );
  }

  return (
    <ChartContainer
      config={chartConfig}
      className={`${CHART_HEIGHT_CLASS} w-full`}
    >
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
      >
        <defs>
          {channels.map((channel) => {
            const color = mapChannelToColor(
              CONSTANT_LABELS[channel] ?? channel,
              channelColorMap
            );

            return (
              <linearGradient
                key={channel}
                id={getGradientId(channel)}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            );
          })}
        </defs>
        {channels.map((channel) => {
          const label = getChannelLabel(channel);

          return (
            <Area
              key={`${channel}-fill`}
              isAnimationActive={false}
              type="monotone"
              dataKey={channel}
              name={label}
              stroke="none"
              fill={`url(#${getGradientId(channel)})`}
              fillOpacity={1}
              dot={false}
              connectNulls
              legendType="none"
              tooltipType="none"
            />
          );
        })}
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
        <XAxis
          dataKey="date"
          axisLine={false}
          tickLine={false}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
          tickFormatter={(date) => dayjs(date).format("DD/MM")}
          interval="preserveStartEnd"
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
          tickFormatter={formatAxisValue}
          width={yAxisWidth}
          allowDecimals={allowYAxisDecimals}
        />
        <ChartTooltip
          position={{ y: 25 }}
          content={(props) => (
            <ComparisonChartTooltipContent
              payload={props.payload}
              label={props.label}
              config={chartConfig}
              isCompared={compared}
              sortOrder={channels}
              labelFormatter={formatTooltipLabel}
              valueFormatter={(value) => formatValue(value)}
            />
          )}
        />
        {channels.map((channel) => {
          const label = getChannelLabel(channel);
          const color = mapChannelToColor(
            CONSTANT_LABELS[channel] ?? channel,
            channelColorMap
          );

          return (
            <Line
              key={channel}
              isAnimationActive={false}
              type="monotone"
              dataKey={channel}
              name={label}
              stroke={color}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          );
        })}
        {compared
          ? channels.map((channel) => {
              const label = getChannelLabel(channel);
              const color = mapChannelToColor(
                CONSTANT_LABELS[channel] ?? channel,
                channelColorMap
              );

              return (
                <Line
                  key={getComparisonMetricKey(channel)}
                  type="monotone"
                  dataKey={getComparisonMetricKey(channel)}
                  name={`${label} (comparison)`}
                  stroke={color}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls
                />
              );
            })
          : null}
        <ChartLegend
          content={(props) => (
            <ComparisonChartLegendContent
              payload={props.payload}
              config={chartConfig}
              isCompared={compared}
              sortOrder={channels}
            />
          )}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
